'use client';

import {
  UseMutationOptions,
  useMutation,
  useQueries,
} from '@tanstack/react-query';

import { useAuth } from '@sola/auth-sdk/store.js';
import { GraphQLSupporter } from '@sola/schema/dotcom/supporters/schema.generated.js';

import { MOCK_SUPPORTER } from '../constants';
import { SupporterQuery } from '../queries';

export const useReadyForQueries = () => {
  const { authUser } = useAuth('authUser');
  return {
    isReady: !!MOCK_SUPPORTER || !!authUser?.authUserGlobalId,
    globalId: authUser?.authUserGlobalId,
  };
};

export const useSupporterQuery = (
  supporterQueries:
    | SupporterQuery<keyof GraphQLSupporter | undefined>
    | SupporterQuery<keyof GraphQLSupporter | undefined>[],
) => {
  const { isReady, globalId } = useReadyForQueries();
  const queries = Array.isArray(supporterQueries)
    ? supporterQueries
    : [supporterQueries];

  const queryResults = useQueries({
    queries: queries.map((supporterQuery) => {
      return {
        queryKey: supporterQuery.queryKey,
        // We should always have a globalId if this runs bc of isReady logic.
        queryFn: () => supporterQuery.queryFn(globalId as string),
        enabled: !!isReady,
        refetchOnWindowFocus: false,
      };
    }),
  });

  return {
    ...(queries.length === 1
      ? queryResults[0]
      : {
          data: queryResults.map((result) => result.data),
          isFetched: queryResults.every((result) => result.isFetched),
          isFetching: queryResults.some((result) => result.isFetching),
          isLoading: queryResults.some((result) => result.isLoading),
          isPending: queryResults.some((result) => result.isPending),
          isPaused: queryResults.some((result) => result.isPaused),
          isError: queryResults.some((result) => result.isError),
          isSuccess: queryResults.every((result) => result.isSuccess),
          queries: queryResults,
        }),
  };
};

export const useSupporterMutation = <
  TData = unknown,
  TError = unknown,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
) => {
  return useMutation<TData, TError, TVariables, TContext>({
    ...options,
    ...(!!MOCK_SUPPORTER && {
      mutationFn: async () => {
        // eslint-disable-next-line no-promise-executor-return
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return true as TData;
      },
      onSuccess: () => alert('Success!'),
    }),
  });
};
